/*
 * AYA — Value Signals tracker (PWA install prompt heuristic)
 * ---------------------------------------------------------------------------
 * L'utilisateur perçoit la valeur d'AYA en effectuant des actions clés :
 *   • Envoi d'un message à AYA
 *   • Vue d'un produit (modal ouverte)
 *   • Ajout au panier
 *   • Suivi d'un marchand
 *   • Découverte d'un live
 *   • Adresse revendiquée
 *
 * Ce module compte ces signaux et notifie les listeners quand un "value moment"
 * est atteint. Il ne se base PAS sur un nombre d'interactions brut, mais sur
 * une combinaison qualitative : au moins 2 signaux distincts OU 1 signal fort
 * (cart_add, address_claimed, follow_merchant, live_watched).
 * ---------------------------------------------------------------------------
 */

const STORAGE_KEY = 'aya_value_signals_v1';
const LISTENERS = new Set();

// Signaux "forts" : déclenchent immédiatement le moment de valeur
const STRONG_SIGNALS = new Set([
  'cart_add', 'address_claimed', 'follow_merchant', 'live_watched', 'order_placed', 'live_started',
]);

// Combos qui déclenchent aussi (2 signaux distincts parmi les moyens)
const MEDIUM_SIGNALS = new Set([
  'chat_message_sent', 'product_viewed', 'live_discovery_opened', 'cart_opened', 'shop_visited',
]);

function _load() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || { signals: {}, milestone_reached_at: null, dismissed_at: null };
  } catch (_) {
    return { signals: {}, milestone_reached_at: null, dismissed_at: null };
  }
}
function _save(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (_) { /* silent */ }
}

function _checkMilestone(state) {
  const signals = Object.keys(state.signals || {});
  if (signals.some((s) => STRONG_SIGNALS.has(s))) return true;
  const mediums = signals.filter((s) => MEDIUM_SIGNALS.has(s));
  return mediums.length >= 2;
}

export function trackSignal(name) {
  const state = _load();
  const already = !!state.signals[name];
  state.signals[name] = { first_at: state.signals[name]?.first_at || Date.now(), count: (state.signals[name]?.count || 0) + 1 };
  if (!state.milestone_reached_at && _checkMilestone(state)) {
    state.milestone_reached_at = Date.now();
    _save(state);
    LISTENERS.forEach((cb) => { try { cb('milestone_reached', name); } catch (_) { /* silent */ } });
    return;
  }
  _save(state);
  if (!already) LISTENERS.forEach((cb) => { try { cb('signal_new', name); } catch (_) { /* silent */ } });
}

export function hasMilestone() {
  const state = _load();
  return !!state.milestone_reached_at;
}

export function wasDismissed() {
  const state = _load();
  if (!state.dismissed_at) return false;
  // Re-propose 7 jours après un dismiss
  const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
  return (Date.now() - state.dismissed_at) < SEVEN_DAYS;
}

export function markDismissed() {
  const state = _load();
  state.dismissed_at = Date.now();
  _save(state);
  LISTENERS.forEach((cb) => { try { cb('dismissed'); } catch (_) { /* silent */ } });
}

export function markInstalled() {
  const state = _load();
  state.installed_at = Date.now();
  _save(state);
}

export function isInstalled() {
  // display-mode: standalone → PWA installée et lancée
  if (typeof window === 'undefined') return false;
  return window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
}

export function subscribeSignals(cb) {
  LISTENERS.add(cb);
  return () => LISTENERS.delete(cb);
}

export function debugState() { return _load(); }
